import { useEffect, useState } from "react";
import api from "../lib/api";
import { Modal, Field } from "./Orders";
import { Plus, TrashSimple, ShieldCheck, User } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { formatDate } from "../lib/format";

export default function Users() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState({
        email: "",
        password: "",
        name: "",
        role: "employee",
    });
    const [error, setError] = useState("");

    const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    useEffect(() => {
        load();
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        try {
            await api.post("/users", form);
            setShowModal(false);
            setForm({ email: "", password: "", name: "", role: "employee" });
            load();
        } catch (e2) {
            setError(e2.response?.data?.detail || "Failed to create user");
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this user?")) return;
        await api.delete(`/users/${id}`);
        load();
    };

    return (
        <div className="space-y-6" data-testid="users-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="label-uppercase">Access</div>
                    <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
                        Team & Roles
                    </h1>
                    <p className="text-slate-400 text-sm mt-1">
                        Manage admin and employee accounts.
                    </p>
                </div>
                <button
                    onClick={() => setShowModal(true)}
                    data-testid="new-user-btn"
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2.5 rounded-xl font-medium btn-glow"
                >
                    <Plus weight="bold" size={16} /> Add User
                </button>
            </div>

            <div className="glass-card overflow-hidden">
                <table className="w-full text-sm" data-testid="users-table">
                    <thead>
                        <tr className="text-left text-slate-400 border-b border-white/5">
                            <th className="px-5 py-3 label-uppercase">User</th>
                            <th className="px-5 py-3 label-uppercase">Email</th>
                            <th className="px-5 py-3 label-uppercase">Role</th>
                            <th className="px-5 py-3 label-uppercase">Created</th>
                            <th className="px-5 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u) => (
                            <tr
                                key={u.id}
                                className="border-b border-white/5 hover:bg-white/[0.03]"
                            >
                                <td className="px-5 py-4">
                                    <div className="flex items-center gap-3">
                                        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 grid place-items-center font-display font-bold text-sm">
                                            {(u.name || "?")[0].toUpperCase()}
                                        </div>
                                        <span className="font-medium">{u.name}</span>
                                    </div>
                                </td>
                                <td className="px-5 py-4 text-slate-300 font-mono text-xs">
                                    {u.email}
                                </td>
                                <td className="px-5 py-4">
                                    <span
                                        className={`status-pill border ${
                                            u.role === "admin"
                                                ? "bg-blue-500/15 text-blue-300 border-blue-500/30"
                                                : "bg-slate-500/15 text-slate-300 border-slate-500/30"
                                        }`}
                                    >
                                        {u.role === "admin" ? (
                                            <ShieldCheck size={12} weight="fill" />
                                        ) : (
                                            <User size={12} weight="fill" />
                                        )}
                                        {u.role}
                                    </span>
                                </td>
                                <td className="px-5 py-4 text-slate-400 text-xs">
                                    {formatDate(u.created_at)}
                                </td>
                                <td className="px-5 py-4 text-right">
                                    {u.id !== me?.id && (
                                        <button
                                            onClick={() => onDelete(u.id)}
                                            className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-rose-400"
                                        >
                                            <TrashSimple size={14} />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <Modal title="Add User" onClose={() => setShowModal(false)}>
                    <form onSubmit={onSubmit} className="space-y-4">
                        <Field label="Name" required>
                            <input
                                required
                                className="input"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                            />
                        </Field>
                        <Field label="Email" required>
                            <input
                                type="email"
                                required
                                className="input"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                            />
                        </Field>
                        <Field label="Password" required>
                            <input
                                type="password"
                                required
                                className="input"
                                value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                            />
                        </Field>
                        <Field label="Role">
                            <select
                                className="input"
                                value={form.role}
                                onChange={(e) => setForm({ ...form, role: e.target.value })}
                            >
                                <option value="employee">Employee</option>
                                <option value="admin">Admin</option>
                            </select>
                        </Field>
                        {error && (
                            <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
                                {String(error)}
                            </div>
                        )}
                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                className="px-4 py-2 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 btn-glow font-medium text-sm"
                            >
                                Create User
                            </button>
                        </div>
                    </form>
                </Modal>
            )}
        </div>
    );
}
